import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import {
  Plus,
  Search,
  BookOpen,
  FileText,
  Layers,
  Target,
  ArrowRight,
  Eye,
  Edit,
  Trash2,
  X,
  ChevronRight,
  Link as LinkIcon,
  ExternalLink,
} from "lucide-react";

const Academia = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("rutas");
  const [routes, setRoutes] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [newRoute, setNewRoute] = useState({
    name: "",
    promise: "",
    layer_1: "",
    layer_2: "",
    layer_3: "",
    layer_4: "",
    layer_5: "",
    target_audience: "",
    duration_weeks: 8,
    kpis: {},
  });
  const [newResource, setNewResource] = useState({
    title: "",
    description: "",
    url: "",
    type: "drive_pdf",
    tags: [],
  });

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [routesRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/routes`, { withCredentials: true }),
        axios.get(`${API}/resources?module=academia`, { withCredentials: true }),
      ]);
      setRoutes(routesRes.data || []);
      setResources(resourcesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Initialize with empty arrays for production
      setRoutes([]);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoute = async () => {
    if (!newRoute.name.trim()) return;
    try {
      const response = await axios.post(`${API}/routes`, newRoute, { withCredentials: true });
      setRoutes([...routes, response.data]);
    } catch (error) {
      console.error("Error creating route:", error);
      const mockRoute = { route_id: `r_${Date.now()}`, ...newRoute, kpis: { completionRate: 0, satisfaction: 0 } };
      setRoutes([...routes, mockRoute]);
    }
    setShowRouteModal(false);
    setEditingRoute(null);
    setNewRoute({ name: "", promise: "", layer_1: "", layer_2: "", layer_3: "", layer_4: "", layer_5: "", target_audience: "", duration_weeks: 8, kpis: {} });
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route);
    setNewRoute({
      name: route.name,
      promise: route.promise || "",
      layer_1: route.layer_1 || "",
      layer_2: route.layer_2 || "",
      layer_3: route.layer_3 || "",
      layer_4: route.layer_4 || "",
      layer_5: route.layer_5 || "",
      target_audience: route.target_audience || "",
      duration_weeks: route.duration_weeks || 8,
      kpis: route.kpis || {},
    });
    setShowRouteModal(true);
  };

  const handleUpdateRoute = async () => {
    if (!editingRoute) return;
    try {
      await axios.put(`${API}/routes/${editingRoute.route_id}`, newRoute, { withCredentials: true });
    } catch (error) {
      console.error("Error updating route:", error);
    }
    setRoutes(routes.map(r => r.route_id === editingRoute.route_id ? { ...r, ...newRoute } : r));
    setShowRouteModal(false);
    setEditingRoute(null);
    setNewRoute({ name: "", promise: "", layer_1: "", layer_2: "", layer_3: "", layer_4: "", layer_5: "", target_audience: "", duration_weeks: 8, kpis: {} });
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta ruta?")) return;
    try {
      await axios.delete(`${API}/routes/${routeId}`, { withCredentials: true });
      setRoutes(routes.filter(r => r.route_id !== routeId));
      if (selectedRoute?.route_id === routeId) setSelectedRoute(null);
      toast.success("Ruta eliminada");
    } catch (error) {
      console.error("Error deleting route:", error);
      toast.error(error.response?.data?.detail || "No se pudo eliminar la ruta");
    }
  };

  const handleCreateResource = async () => {
    if (!newResource.title.trim()) return;
    try {
      const response = await axios.post(`${API}/resources`, { ...newResource, module: "academia" }, { withCredentials: true });
      setResources([...resources, response.data]);
    } catch (error) {
      console.error("Error creating resource:", error);
      const mockResource = { resource_id: `res_${Date.now()}`, ...newResource };
      setResources([...resources, mockResource]);
    }
    setShowResourceModal(false);
    setEditingResource(null);
    setNewResource({ title: "", description: "", url: "", type: "drive_pdf", tags: [] });
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setNewResource({
      title: resource.title,
      description: resource.description || "",
      url: resource.url || "",
      type: resource.type || "drive_pdf",
      tags: resource.tags || [],
    });
    setShowResourceModal(true);
  };

  const handleUpdateResource = async () => {
    if (!editingResource) return;
    try {
      await axios.put(`${API}/resources/${editingResource.resource_id}`, newResource, { withCredentials: true });
    } catch (error) {
      console.error("Error updating resource:", error);
    }
    setResources(resources.map(r => r.resource_id === editingResource.resource_id ? { ...r, ...newResource } : r));
    setShowResourceModal(false);
    setEditingResource(null);
    setNewResource({ title: "", description: "", url: "", type: "drive_pdf", tags: [] });
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm("¿Estás seguro de eliminar este recurso?")) return;
    try {
      await axios.delete(`${API}/resources/${resourceId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting resource:", error);
    }
    setResources(resources.filter(r => r.resource_id !== resourceId));
  };

  const getResourceIcon = (type) => {
    const icons = {
      drive_pdf: FileText,
      drive_doc: FileText,
      drive_sheet: Layers,
      youtube: Eye,
    };
    return icons[type] || FileText;
  };

  const getEmbedUrl = (url, type) => {
    if (!url) return null;
    
    // Google Drive files
    if (url.includes("docs.google.com") || url.includes("drive.google.com")) {
      const fileId = url.match(/[-\w]{25,}/);
      if (fileId) {
        if (type === "drive_pdf" || url.includes("/document/")) {
          return `https://docs.google.com/document/d/${fileId[0]}/preview`;
        }
        if (type === "drive_sheet" || url.includes("/spreadsheets/")) {
          return `https://docs.google.com/spreadsheets/d/${fileId[0]}/preview`;
        }
        return `https://drive.google.com/file/d/${fileId[0]}/preview`;
      }
    }
    
    // YouTube videos
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId[1]}`;
      }
    }
    
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="academia">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Academia</h1>
          <p className="text-slate-500">Builder de rutas educativas y repositorio de recursos</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        {[
          { id: "rutas", label: "Builder de Rutas", icon: Target },
          { id: "recursos", label: "Repositorio", icon: FileText },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Rutas Tab */}
      {activeTab === "rutas" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setShowRouteModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-route-btn"
            >
              <Plus size={20} />
              Nueva Ruta
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {routes.map((route) => (
              <div
                key={route.route_id}
                className="glass-panel overflow-hidden card-hover cursor-pointer"
                onClick={() => setSelectedRoute(route)}
                data-testid={`route-${route.route_id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <BookOpen size={24} className="text-white" />
                    </div>
                    <span className="badge badge-primary">
                      {route.duration_weeks} semanas
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    {route.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">{route.promise}</p>
                  
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((layer) => (
                      <div key={layer} className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          layer <= 2 ? "bg-primary/10 text-primary" :
                          layer <= 4 ? "bg-secondary/20 text-secondary-700" :
                          "bg-emerald-100 text-emerald-700"
                        }`}>
                          {layer}
                        </div>
                        <span className="text-sm text-slate-600 truncate">
                          {route[`layer_${layer}`]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-sm text-slate-500">
                    Audiencia: {route.target_audience}
                  </span>
                  <div className="flex items-center gap-2">
                    {isSuperAdmin && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditRoute(route); }}
                          className="p-1.5 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteRoute(route.route_id); }}
                          className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                    <ChevronRight size={18} className="text-slate-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recursos Tab */}
      {activeTab === "recursos" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar recursos..."
                className="w-full glass-input pl-10"
                data-testid="search-resources-input"
              />
            </div>
            <button
              onClick={() => setShowResourceModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-resource-btn"
            >
              <Plus size={20} />
              Agregar Recurso
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => {
              const Icon = getResourceIcon(resource.type);
              return (
                <div
                  key={resource.resource_id}
                  className="glass-panel p-6 card-hover"
                  data-testid={`resource-${resource.resource_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={24} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">
                        {resource.title}
                      </h3>
                      <p className="text-sm text-slate-500 capitalize">
                        {resource.type.replace("drive_", "").replace("_", " ")}
                      </p>
                    </div>
                  </div>
                  
                  {resource.description && (
                    <p className="text-sm text-slate-600 mt-4 line-clamp-2">
                      {resource.description}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 mt-4">
                    {resource.tags?.map((tag, i) => (
                      <span key={i} className="badge bg-slate-100 text-slate-600 text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end gap-2">
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost text-sm flex items-center gap-1"
                    >
                      <ExternalLink size={16} />
                      Abrir
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Route Detail Modal */}
      {selectedRoute && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">{selectedRoute.name}</h2>
              <button
                onClick={() => setSelectedRoute(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              <div className="p-4 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5">
                <h3 className="font-semibold text-slate-700 mb-2">Promesa</h3>
                <p className="text-slate-600">{selectedRoute.promise}</p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-700 mb-4">Capas de Aprendizaje</h3>
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((layer) => (
                    <div key={layer} className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50/50">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold ${
                        layer <= 2 ? "bg-primary text-white" :
                        layer <= 4 ? "bg-secondary text-slate-800" :
                        "bg-emerald-500 text-white"
                      }`}>
                        {layer}
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-800">Capa {layer}</h4>
                        <p className="text-sm text-slate-600">{selectedRoute[`layer_${layer}`]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-slate-50/50">
                  <p className="text-sm text-slate-500 mb-1">Audiencia</p>
                  <p className="font-medium text-slate-800">{selectedRoute.target_audience}</p>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50/50">
                  <p className="text-sm text-slate-500 mb-1">Duración</p>
                  <p className="font-medium text-slate-800">{selectedRoute.duration_weeks} semanas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Route Modal */}
      {showRouteModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up" data-testid="create-route-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">Nueva Ruta Educativa</h2>
              <button
                onClick={() => setShowRouteModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre de la Ruta</label>
                <input
                  type="text"
                  value={newRoute.name}
                  onChange={(e) => setNewRoute({ ...newRoute, name: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Ej: Transformación Digital"
                  data-testid="route-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Promesa</label>
                <textarea
                  value={newRoute.promise}
                  onChange={(e) => setNewRoute({ ...newRoute, promise: e.target.value })}
                  className="w-full glass-input min-h-[80px]"
                  placeholder="¿Qué lograrán los participantes?"
                  data-testid="route-promise-input"
                />
              </div>

              {[1, 2, 3, 4, 5].map((layer) => (
                <div key={layer}>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Capa {layer}
                  </label>
                  <input
                    type="text"
                    value={newRoute[`layer_${layer}`]}
                    onChange={(e) => setNewRoute({ ...newRoute, [`layer_${layer}`]: e.target.value })}
                    className="w-full glass-input"
                    placeholder={`Contenido de la capa ${layer}`}
                    data-testid={`route-layer-${layer}-input`}
                  />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Audiencia</label>
                  <input
                    type="text"
                    value={newRoute.target_audience}
                    onChange={(e) => setNewRoute({ ...newRoute, target_audience: e.target.value })}
                    className="w-full glass-input"
                    placeholder="Ej: Empresas PYME"
                    data-testid="route-audience-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Duración (semanas)</label>
                  <input
                    type="number"
                    value={newRoute.duration_weeks}
                    onChange={(e) => setNewRoute({ ...newRoute, duration_weeks: parseInt(e.target.value) })}
                    className="w-full glass-input"
                    data-testid="route-duration-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setShowRouteModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button onClick={handleCreateRoute} className="btn-primary" data-testid="save-route-btn">
                  Crear Ruta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Resource Modal */}
      {showResourceModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-lg animate-slide-up" data-testid="add-resource-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">Agregar Recurso</h2>
              <button
                onClick={() => setShowResourceModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título</label>
                <input
                  type="text"
                  value={newResource.title}
                  onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Nombre del recurso"
                  data-testid="resource-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo</label>
                <select
                  value={newResource.type}
                  onChange={(e) => setNewResource({ ...newResource, type: e.target.value })}
                  className="w-full glass-input"
                  data-testid="resource-type-select"
                >
                  <option value="drive_pdf">PDF (Google Drive)</option>
                  <option value="drive_doc">Documento (Google Drive)</option>
                  <option value="drive_sheet">Hoja de Cálculo (Google Drive)</option>
                  <option value="youtube">Video (YouTube)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">URL del Recurso</label>
                <input
                  type="url"
                  value={newResource.url}
                  onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                  className="w-full glass-input"
                  placeholder="https://..."
                  data-testid="resource-url-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                  className="w-full glass-input min-h-[80px]"
                  placeholder="Descripción breve del recurso"
                  data-testid="resource-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setShowResourceModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button onClick={handleCreateResource} className="btn-primary" data-testid="save-resource-btn">
                  Agregar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Academia;
